/**
 * Language registry — Tier 1 / Tier 2 with fallback chains ending in en.
 *
 * The built-in entries are defaults until the server registry loads
 * (Translation Admin → /i18n/languages). Only `published` languages appear in
 * normal pickers; translation admins also see their assigned languages as preview.
 */

export type LanguageStatus = 'under_construction' | 'ready' | 'published';

export interface LanguageDef {
  code: string;
  name: string;
  nativeName: string;
  /** Ordered fallbacks; every chain ends at en (en has empty fallbacks). */
  fallbacks: string[];
  status: LanguageStatus;
  tier: 1 | 2;
}

/** Shape returned by GET /i18n/languages and /i18n/me. */
export interface RemoteLanguage {
  code: string;
  name: string;
  native_name: string;
  fallbacks: string[];
  tier: number;
  status: string;
}

const ENGLISH: LanguageDef = {
  code: 'en',
  name: 'English',
  nativeName: 'English',
  fallbacks: [],
  status: 'published',
  tier: 1,
};

/** Built-in defaults (used before the server registry has been fetched). */
export const BUILT_IN_LANGUAGES: Record<string, LanguageDef> = {
  en: ENGLISH,
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    fallbacks: ['en'],
    status: 'under_construction',
    tier: 1,
  },
};

const REGISTRY_CACHE_KEY = 'rc_i18n_languages';
const PREVIEW_CACHE_KEY = 'rc_i18n_preview_langs';
const LINK_PREVIEW_KEY = 'rc_i18n_link_preview';

let registry: Record<string, LanguageDef> = { ...BUILT_IN_LANGUAGES };
/** Codes the signed-in user may preview before publish (assigned admin / manager). */
let previewCodes: Set<string> = new Set();
/** Code opened with a ?preview=<code> link; lasts for this tab's session, signed in or not. */
let linkPreviewCode: string | null = null;

export const DEFAULT_LANGUAGE = 'en';

function toLanguageDef(remote: RemoteLanguage): LanguageDef | null {
  if (!remote?.code || remote.code === 'en') return null;
  const status: LanguageStatus =
    remote.status === 'published' || remote.status === 'ready' ? remote.status : 'under_construction';
  return {
    code: remote.code,
    name: remote.name || remote.code,
    nativeName: remote.native_name || remote.name || remote.code,
    fallbacks: remote.fallbacks?.length ? remote.fallbacks : ['en'],
    status,
    tier: remote.tier === 2 ? 2 : 1,
  };
}

function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / privacy mode */
  }
}

function buildRegistry(remote: RemoteLanguage[]): Record<string, LanguageDef> {
  const defs = remote.map(toLanguageDef).filter((l): l is LanguageDef => l !== null);
  if (defs.length === 0) return { ...BUILT_IN_LANGUAGES };
  const next: Record<string, LanguageDef> = { en: ENGLISH };
  for (const def of defs) next[def.code] = def;
  return next;
}

/** Replace non-English entries with the server registry (empty → built-in defaults). */
export function applyRemoteLanguages(remote: RemoteLanguage[]): void {
  registry = buildRegistry(remote);
  writeCache(REGISTRY_CACHE_KEY, remote);
}

/** Languages this user may preview before they are published. */
export function setPreviewLanguages(remote: RemoteLanguage[] | null): void {
  const list = remote ?? [];
  previewCodes = new Set(list.map((l) => l.code).filter((c) => c && c !== 'en'));
  for (const lang of list) {
    const def = toLanguageDef(lang);
    if (def && !registry[def.code]) registry[def.code] = def;
  }
  writeCache(PREVIEW_CACHE_KEY, remote ? list : null);
}

/**
 * Apply a ?preview= link value: a language code turns on preview for this tab's
 * session; "off" turns it off. Returns the code now being link-previewed, if any.
 */
export function applyPreviewLink(value: string | null): string | null {
  if (value === null) return linkPreviewCode;
  try {
    if (value === 'off' || value === '' || value === 'en') {
      linkPreviewCode = null;
      sessionStorage.removeItem(LINK_PREVIEW_KEY);
    } else {
      linkPreviewCode = value;
      sessionStorage.setItem(LINK_PREVIEW_KEY, value);
    }
  } catch {
    /* ignore privacy mode */
  }
  return linkPreviewCode;
}

function hasPreviewAccess(code: string): boolean {
  return previewCodes.has(code) || (linkPreviewCode === code && !!registry[code]);
}

// Hydrate from the last fetch so first paint matches the last known registry.
(() => {
  const cachedRegistry = readCache<RemoteLanguage[]>(REGISTRY_CACHE_KEY);
  if (cachedRegistry) registry = buildRegistry(cachedRegistry);
  const cachedPreview = readCache<RemoteLanguage[]>(PREVIEW_CACHE_KEY);
  if (cachedPreview) setPreviewLanguages(cachedPreview);
  try {
    linkPreviewCode = sessionStorage.getItem(LINK_PREVIEW_KEY);
  } catch {
    linkPreviewCode = null;
  }
})();

export function getLanguage(code: string): LanguageDef | undefined {
  return registry[code];
}

export function getFallbackChain(locale: string): string[] {
  const chain: string[] = [];
  let current: string | undefined = locale;
  const seen = new Set<string>();
  while (current && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    const langDef: LanguageDef | undefined = registry[current];
    current = langDef?.fallbacks[0];
  }
  if (!chain.includes('en')) {
    chain.push('en');
  }
  return chain;
}

/** Languages shown to everyone (pickers / offer banner). */
export function getPublishedLanguages(): LanguageDef[] {
  return Object.values(registry).filter((l) => l.status === 'published');
}

/** Published languages plus any this user may preview. */
export function getSelectableLanguages(): LanguageDef[] {
  return Object.values(registry).filter(
    (l) => l.status === 'published' || hasPreviewAccess(l.code)
  );
}

export function isPreviewLanguage(code: string): boolean {
  const def = registry[code];
  return !!def && def.status !== 'published' && hasPreviewAccess(code);
}

export function isLanguageSelectable(code: string): boolean {
  return getSelectableLanguages().some((l) => l.code === code);
}

/** Best published match from the browser Accept-Language list. */
export function negotiateBrowserLanguage(browserLangs: readonly string[]): string {
  const byLower = new Map(getPublishedLanguages().map((l) => [l.code.toLowerCase(), l.code]));

  for (const raw of browserLangs) {
    const lower = raw.toLowerCase();
    const match = byLower.get(lower) ?? byLower.get(lower.split('-')[0]);
    if (match) return match;
  }
  return DEFAULT_LANGUAGE;
}
